import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  XCircle,
  Clock,
  CreditCard,
  Users,
  UserCheck,
  RefreshCw,
  Eye,
  ChevronRight,
  AlertCircle,
  DollarSign,
  FileText,
  Search,
  Mail,
  Phone,
  Edit,
  Save,
  X,
  QrCode,
  Copy,
  Check,
  Download,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import html2canvas from "html2canvas";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGES = [
  { key: "pending_approval", label: "Pending Approval", icon: Clock, color: "amber" },
  { key: "awaiting_payment", label: "Awaiting Payment", icon: CreditCard, color: "blue" },
  { key: "payment_verification", label: "Payment Verification", icon: DollarSign, color: "purple" },
  { key: "child_assignment", label: "Child Assignment", icon: Users, color: "orange" },
  { key: "complete", label: "Complete", icon: CheckCircle, color: "emerald" },
];

export default function RegistrationPipeline({ user }) {
  const [pipeline, setPipeline] = useState(null);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState("pending_approval");
  const [selectedReg, setSelectedReg] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectDialog, setRejectDialog] = useState({ open: false, reg: null });
  
  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // QR Code dialog state
  const [qrDialog, setQrDialog] = useState({ open: false, reg: null });
  const [copiedField, setCopiedField] = useState(null);
  
  // Ref for QR download
  const pipelineQrRef = useRef(null);
  const [downloadingQr, setDownloadingQr] = useState(false);

  // Download QR code as image
  const downloadPaymentQR = async (reg) => {
    if (!pipelineQrRef.current) return;
    
    setDownloadingQr(true);
    try {
      const canvas = await html2canvas(pipelineQrRef.current, {
        backgroundColor: '#1e293b',
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      const refNum = reg?.payment_reference || reg?.id?.slice(-8) || 'payment';
      const link = document.createElement('a');
      link.download = `SCC_Payment_${refNum}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success("Payment QR saved!");
    } catch (error) {
      console.error("Error downloading QR:", error);
      toast.error("Failed to save QR image");
    } finally {
      setDownloadingQr(false);
    }
  };

  // CRC16 lookup table for PayNow QR code
  const crcTable = [
    0x0000, 0x1021, 0x2042, 0x3063, 0x4084, 0x50a5, 0x60c6, 0x70e7,
    0x8108, 0x9129, 0xa14a, 0xb16b, 0xc18c, 0xd1ad, 0xe1ce, 0xf1ef,
    0x1231, 0x0210, 0x3273, 0x2252, 0x52b5, 0x4294, 0x72f7, 0x62d6,
    0x9339, 0x8318, 0xb37b, 0xa35a, 0xd3bd, 0xc39c, 0xf3ff, 0xe3de,
    0x2462, 0x3443, 0x0420, 0x1401, 0x64e6, 0x74c7, 0x44a4, 0x5485,
    0xa56a, 0xb54b, 0x8528, 0x9509, 0xe5ee, 0xf5cf, 0xc5ac, 0xd58d,
    0x3653, 0x2672, 0x1611, 0x0630, 0x76d7, 0x66f6, 0x5695, 0x46b4,
    0xb75b, 0xa77a, 0x9719, 0x8738, 0xf7df, 0xe7fe, 0xd79d, 0xc7bc,
    0x48c4, 0x58e5, 0x6886, 0x78a7, 0x0840, 0x1861, 0x2802, 0x3823,
    0xc9cc, 0xd9ed, 0xe98e, 0xf9af, 0x8948, 0x9969, 0xa90a, 0xb92b,
    0x5af5, 0x4ad4, 0x7ab7, 0x6a96, 0x1a71, 0x0a50, 0x3a33, 0x2a12,
    0xdbfd, 0xcbdc, 0xfbbf, 0xeb9e, 0x9b79, 0x8b58, 0xbb3b, 0xab1a,
    0x6ca6, 0x7c87, 0x4ce4, 0x5cc5, 0x2c22, 0x3c03, 0x0c60, 0x1c41,
    0xedae, 0xfd8f, 0xcdec, 0xddcd, 0xad2a, 0xbd0b, 0x8d68, 0x9d49,
    0x7e97, 0x6eb6, 0x5ed5, 0x4ef4, 0x3e13, 0x2e32, 0x1e51, 0x0e70,
    0xff9f, 0xefbe, 0xdfdd, 0xcffc, 0xbf1b, 0xaf3a, 0x9f59, 0x8f78,
    0x9188, 0x81a9, 0xb1ca, 0xa1eb, 0xd10c, 0xc12d, 0xf14e, 0xe16f,
    0x1080, 0x00a1, 0x30c2, 0x20e3, 0x5004, 0x4025, 0x7046, 0x6067,
    0x83b9, 0x9398, 0xa3fb, 0xb3da, 0xc33d, 0xd31c, 0xe37f, 0xf35e,
    0x02b1, 0x1290, 0x22f3, 0x32d2, 0x4235, 0x5214, 0x6277, 0x7256,
    0xb5ea, 0xa5cb, 0x95a8, 0x8589, 0xf56e, 0xe54f, 0xd52c, 0xc50d,
    0x34e2, 0x24c3, 0x14a0, 0x0481, 0x7466, 0x6447, 0x5424, 0x4405,
    0xa7db, 0xb7fa, 0x8799, 0x97b8, 0xe75f, 0xf77e, 0xc71d, 0xd73c,
    0x26d3, 0x36f2, 0x0691, 0x16b0, 0x6657, 0x7676, 0x4615, 0x5634,
    0xd94c, 0xc96d, 0xf90e, 0xe92f, 0x99c8, 0x89e9, 0xb98a, 0xa9ab,
    0x5844, 0x4865, 0x7806, 0x6827, 0x18c0, 0x08e1, 0x3882, 0x28a3,
    0xcb7d, 0xdb5c, 0xeb3f, 0xfb1e, 0x8bf9, 0x9bd8, 0xabbb, 0xbb9a,
    0x4a75, 0x5a54, 0x6a37, 0x7a16, 0x0af1, 0x1ad0, 0x2ab3, 0x3a92,
    0xfd2e, 0xed0f, 0xdd6c, 0xcd4d, 0xbdaa, 0xad8b, 0x9de8, 0x8dc9,
    0x7c26, 0x6c07, 0x5c64, 0x4c45, 0x3ca2, 0x2c83, 0x1ce0, 0x0cc1,
    0xef1f, 0xff3e, 0xcf5d, 0xdf7c, 0xaf9b, 0xbfba, 0x8fd9, 0x9ff8,
    0x6e17, 0x7e36, 0x4e55, 0x5e74, 0x2e93, 0x3eb2, 0x0ed1, 0x1ef0
  ];

  const calculateCRC16 = (str) => {
    let crc = 0xFFFF;
    for (let i = 0; i < str.length; i++) {
      const c = str.charCodeAt(i);
      const j = (c ^ (crc >> 8)) & 0xFF;
      crc = crcTable[j] ^ (crc << 8);
    }
    return ((crc ^ 0) & 0xFFFF).toString(16).toUpperCase().padStart(4, '0');
  };

  const padLeft = (str, n) => str.toString().padStart(n, '0');

  const generatePayNowQRData = (amount, reference) => {
    const amountStr = parseFloat(amount).toFixed(2);
    const payload = [
      { id: '00', value: '01' },
      { id: '01', value: '12' },
      { id: '26', value: [
        { id: '00', value: 'SG.PAYNOW' },
        { id: '01', value: '3' },
        { id: '02', value: 'UEN201422384R#WISE' },
        { id: '03', value: '1' }
      ]},
      { id: '52', value: '0000' },
      { id: '53', value: '702' },
      { id: '54', value: amountStr },
      { id: '58', value: 'SG' },
      { id: '59', value: 'FOOBALL AND MORE' },
      { id: '60', value: 'Singapore' },
      // Tag 62/01 is the PayNow proxy/account-routing field. Hardcoded to
      // "A21882030QR" — Wise's fixed account identifier. Encoding the
      // SCC-XXX reference here causes Wise to reject and return the payment.
      { id: '62', value: [{ id: '01', value: 'A21882030QR' }]}
    ];
    const buildTLV = (items) => items.map(item => {
      const value = Array.isArray(item.value) ? buildTLV(item.value) : item.value;
      return `${item.id}${padLeft(value.length, 2)}${value}`;
    }).join('');
    const dataWithoutCRC = buildTLV(payload) + '6304';
    return dataWithoutCRC + calculateCRC16(dataWithoutCRC);
  };

  const copyToClipboard = (text, fieldId) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 2000);
    toast.success("Copied to clipboard!");
  };

  useEffect(() => {
    fetchPipeline();
  }, []);

  const fetchPipeline = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/academy/admin/registration-pipeline`, { withCredentials: true });
      setPipeline(res.data.pipeline);
      setCounts(res.data.counts);
    } catch (error) {
      console.error("Error fetching pipeline:", error);
      toast.error("Failed to load registration pipeline");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRegistration = async (regId) => {
    setActionLoading(regId);
    try {
      await axios.post(`${API}/academy/admin/approve-registration/${regId}`, {}, { withCredentials: true });
      toast.success("Registration approved! Parent can now proceed to payment.");
      fetchPipeline();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve registration");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectRegistration = async () => {
    if (!rejectDialog.reg) return;
    setActionLoading(rejectDialog.reg.id);
    try {
      await axios.post(
        `${API}/academy/admin/reject-registration/${rejectDialog.reg.id}`,
        { reason: rejectionReason },
        { withCredentials: true }
      );
      toast.success("Registration rejected");
      setRejectDialog({ open: false, reg: null });
      setRejectionReason("");
      fetchPipeline();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject registration");
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerifyPayment = async (transactionId, action) => {
    setActionLoading(transactionId);
    try {
      await axios.post(
        `${API}/payments/paynow/verify/${transactionId}?action=${action}`,
        {},
        { withCredentials: true }
      );
      toast.success(action === "approve" ? "Payment verified!" : "Payment rejected");
      fetchPipeline();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to verify payment");
    } finally {
      setActionLoading(null);
    }
  };

  // Start editing a registration
  const handleStartEdit = (reg) => {
    setEditMode(true);
    setEditData({
      id: reg.id,
      parent_name: reg.parentName || reg.parent_name || "",
      parent_email: reg.parentEmail || reg.parent_email || "",
      parent_phone: reg.parent?.phone || reg.parent_phone || "",
      children: (reg.children || []).map(c => ({
        id: c.id,
        given_name: c.given_name || "",
        last_name: c.last_name || "",
        name: c.name || c.child_name || `${c.given_name || ""} ${c.last_name || ""}`.trim(),
        birthdate: c.birthdate || "",
        gender: c.gender || "",
        nationality: c.nationality || "",
        school: c.school || "",
        email: c.email || "",
        phone: c.phone || "",
        medical_conditions: c.medical_conditions || c.medical_info || "",
        emergency_contact_name: c.emergency_contact_name || "",
        emergency_contact: c.emergency_contact || "",
        session_ids: c.session_ids || [],
        fee: c.fee || 0,
        membership_number: c.membership_number || ""
      })),
      is_scc_member: reg.is_scc_member || false,
      membership_number: reg.membership_number || ""
    });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditData(null);
  };

  // Save edited registration
  const handleSaveEdit = async () => {
    if (!editData) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/academy/admin/registration/${editData.id}`,
        editData,
        { withCredentials: true }
      );
      toast.success("Registration updated successfully!");
      setEditMode(false);
      setEditData(null);
      fetchPipeline();
      // Refresh selected reg
      if (selectedReg && selectedReg.id === editData.id) {
        setSelectedReg(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update registration");
    } finally {
      setSaving(false);
    }
  };

  // Update edit data for a specific field
  const updateEditField = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  // Update child in edit data
  const updateEditChild = (childIndex, field, value) => {
    setEditData(prev => {
      const newChildren = [...prev.children];
      newChildren[childIndex] = { ...newChildren[childIndex], [field]: value };
      // Also update the name field if given_name or last_name changes
      if (field === "given_name" || field === "last_name") {
        const gn = field === "given_name" ? value : newChildren[childIndex].given_name;
        const ln = field === "last_name" ? value : newChildren[childIndex].last_name;
        newChildren[childIndex].name = `${gn} ${ln}`.trim();
      }
      return { ...prev, children: newChildren };
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-SG", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;

  const getMemberBadge = (reg) => {
    if (reg.member_type === "section") {
      return <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">Section</Badge>;
    } else if (reg.is_scc_member || reg.member_type === "scc_member") {
      return <Badge className="bg-blue-500/20 text-blue-400 text-xs">Club Member</Badge>;
    } else if (reg.member_type === "digital") {
      return <Badge className="bg-purple-500/20 text-purple-400 text-xs">Digital</Badge>;
    }
    return <Badge className="bg-slate-500/20 text-slate-400 text-xs">Non-Member</Badge>;
  };

  const filterRegistrations = (regs) => {
    if (!searchQuery) return regs;
    const q = searchQuery.toLowerCase();
    return regs.filter((r) =>
      r.parentName?.toLowerCase().includes(q) ||
      r.parentEmail?.toLowerCase().includes(q) ||
      r.child_names?.some((name) => name.toLowerCase().includes(q)) ||
      r.id?.toLowerCase().includes(q)
    );
  };

  const renderRegistrationCard = (reg, stage) => {
    const isSelected = selectedReg?.id === reg.id;
    
    return (
      <Card
        key={reg.id}
        className={`bg-slate-800/50 border-slate-700 p-4 cursor-pointer transition-all hover:border-slate-600 ${
          isSelected ? "border-amber-500 ring-1 ring-amber-500/50" : ""
        }`}
        onClick={() => setSelectedReg(reg)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-white truncate">{reg.parentName || "Unknown Parent"}</span>
              {getMemberBadge(reg)}
            </div>
            <div className="text-sm text-slate-400 truncate">{reg.parentEmail}</div>
            <div className="text-xs text-slate-400 mt-1">
              {reg.child_names?.length || 0} child(ren): {reg.child_names?.join(", ") || "N/A"}
            </div>
            {reg.term?.name && (
              <div className="text-xs text-blue-400 mt-1">{reg.term.name}</div>
            )}
          </div>
          <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
        </div>

        {/* Stage-specific info */}
        {stage === "payment_verification" && reg.payment && (
          <div className="mt-3 pt-3 border-t border-slate-700">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">Amount:</span>
              <span className="font-bold text-emerald-400">{formatCurrency(reg.payment.amount)}</span>
            </div>
            <div className="text-xs text-slate-400 mt-1">
              Ref: {reg.payment.payment_reference || "N/A"}
            </div>
          </div>
        )}

        <div className="text-xs text-slate-600 mt-2">
          {formatDate(reg.created_at)}
        </div>
      </Card>
    );
  };

  const renderDetailPanel = () => {
    if (!selectedReg) {
      return (
        <div className="flex items-center justify-center h-full text-slate-400">
          <div className="text-center">
            <Eye className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Select a registration to view details</p>
          </div>
        </div>
      );
    }

    const reg = selectedReg;
    const currentStage = Object.keys(pipeline || {}).find(
      (stage) => pipeline[stage]?.some((r) => r.id === reg.id)
    );

    // EDIT MODE
    if (editMode && editData && editData.id === reg.id) {
      return (
        <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(100vh-200px)]">
          {/* Edit Header */}
          <div className="flex items-center justify-between border-b border-slate-700 pb-3">
            <h3 className="text-lg font-bold text-amber-400 flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Registration
            </h3>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCancelEdit}
                className="text-slate-400"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveEdit}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {saving ? (
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Save
              </Button>
            </div>
          </div>

          {/* Edit Parent Info */}
          <div className="bg-slate-900/50 rounded-lg p-3">
            <h4 className="text-sm font-medium text-slate-300 mb-3">Parent Information</h4>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Parent Name</label>
                <Input
                  value={editData.parent_name}
                  onChange={(e) => updateEditField("parent_name", e.target.value)}
                  className="bg-slate-800 border-slate-600 text-sm"
                  placeholder="Parent full name"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Email</label>
                <Input
                  value={editData.parent_email}
                  onChange={(e) => updateEditField("parent_email", e.target.value)}
                  className="bg-slate-800 border-slate-600 text-sm"
                  placeholder="parent@email.com"
                  disabled
                />
                <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Phone</label>
                <Input
                  value={editData.parent_phone}
                  onChange={(e) => updateEditField("parent_phone", e.target.value)}
                  className="bg-slate-800 border-slate-600 text-sm"
                  placeholder="+65 9123 4567"
                />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="edit-scc-member"
                  checked={editData.is_scc_member || false}
                  onChange={(e) => updateEditField("is_scc_member", e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800"
                />
                <label htmlFor="edit-scc-member" className="text-sm text-slate-300">
                  SCC Member
                </label>
                {editData.is_scc_member && (
                  <Input
                    value={editData.membership_number || ""}
                    onChange={(e) => updateEditField("membership_number", e.target.value)}
                    className="bg-slate-800 border-slate-600 text-sm w-32"
                    placeholder="Member #"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Edit Children */}
          <div className="bg-slate-900/50 rounded-lg p-3">
            <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Children ({editData.children?.length || 0})
            </h4>
            <div className="space-y-4">
              {editData.children?.map((child, idx) => (
                <div key={idx} className="bg-slate-800/50 p-3 rounded-lg border border-slate-700">
                  <div className="text-xs text-slate-500 mb-2">Child {idx + 1}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Given Name</label>
                      <Input
                        value={child.given_name}
                        onChange={(e) => updateEditChild(idx, "given_name", e.target.value)}
                        className="bg-slate-900 border-slate-600 text-sm"
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Last Name</label>
                      <Input
                        value={child.last_name}
                        onChange={(e) => updateEditChild(idx, "last_name", e.target.value)}
                        className="bg-slate-900 border-slate-600 text-sm"
                        placeholder="Last name"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Date of Birth</label>
                      <Input
                        type="date"
                        value={child.birthdate}
                        onChange={(e) => updateEditChild(idx, "birthdate", e.target.value)}
                        className="bg-slate-900 border-slate-600 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Gender</label>
                      <select
                        value={child.gender}
                        onChange={(e) => updateEditChild(idx, "gender", e.target.value)}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-md text-sm text-white"
                      >
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">School</label>
                      <Input
                        value={child.school || ""}
                        onChange={(e) => updateEditChild(idx, "school", e.target.value)}
                        className="bg-slate-900 border-slate-600 text-sm"
                        placeholder="School name"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Nationality</label>
                      <Input
                        value={child.nationality || ""}
                        onChange={(e) => updateEditChild(idx, "nationality", e.target.value)}
                        className="bg-slate-900 border-slate-600 text-sm"
                        placeholder="Nationality"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-slate-400 mb-1 block">Medical Conditions</label>
                      <Input
                        value={child.medical_conditions || ""}
                        onChange={(e) => updateEditChild(idx, "medical_conditions", e.target.value)}
                        className="bg-slate-900 border-slate-600 text-sm"
                        placeholder="None / Asthma / etc."
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Emergency Contact</label>
                      <Input
                        value={child.emergency_contact_name || ""}
                        onChange={(e) => updateEditChild(idx, "emergency_contact_name", e.target.value)}
                        className="bg-slate-900 border-slate-600 text-sm"
                        placeholder="Contact name"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Emergency Phone</label>
                      <Input
                        value={child.emergency_contact || ""}
                        onChange={(e) => updateEditChild(idx, "emergency_contact", e.target.value)}
                        className="bg-slate-900 border-slate-600 text-sm"
                        placeholder="Phone number"
                      />
                    </div>
                  </div>
                  {child.membership_number && (
                    <div className="mt-2 text-xs text-slate-500">
                      Membership #: <span className="text-amber-400 font-mono">{child.membership_number}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // VIEW MODE (original code)
    return (
      <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(100vh-200px)]">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">{reg.parentName}</h3>
            <p className="text-sm text-slate-400">{reg.parentEmail}</p>
            {reg.parent?.phone && (
              <p className="text-sm text-slate-400 flex items-center gap-1 mt-1">
                <Phone className="h-3 w-3" /> {reg.parent.phone}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {getMemberBadge(reg)}
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleStartEdit(reg)}
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
            >
              <Edit className="h-3 w-3 mr-1" />
              Edit
            </Button>
          </div>
        </div>

        {/* Children */}
        <div className="bg-slate-900/50 rounded-lg p-3">
          <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
            <Users className="h-4 w-4" />
            Children ({reg.children?.length || 0})
          </h4>
          <div className="space-y-2">
            {reg.children?.map((child, idx) => {
              const name = child.child_name || child.name ||
                `${child.given_name || ""} ${child.last_name || ""}`.trim() ||
                `${child.first_name || ""} ${child.family_name || ""}`.trim() || "Unnamed";
              return (
                <div key={idx} className="bg-slate-800/50 p-2 rounded text-sm">
                  <div className="font-medium text-white">{name}</div>
                  <div className="text-xs text-slate-400">
                    DOB: {child.birthdate || "N/A"} | Gender: {child.gender || "N/A"}
                  </div>
                  {child.membership_number && (
                    <div className="text-xs text-amber-400 mt-1">
                      #{child.membership_number}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Term & Sessions */}
        {reg.term && (
          <div className="bg-slate-900/50 rounded-lg p-3">
            <h4 className="text-sm font-medium text-slate-300 mb-2">Term</h4>
            <p className="text-white">{reg.term.name}</p>
          </div>
        )}

        {/* Payment Info */}
        {reg.payment && (
          <div className="bg-slate-900/50 rounded-lg p-3">
            <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Payment
            </h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Amount:</span>
                <span className="text-emerald-400 font-bold">{formatCurrency(reg.payment.amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Reference:</span>
                <span className="text-white font-mono">{reg.payment.payment_reference}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Status:</span>
                <Badge className={
                  reg.payment.payment_status === "paid" ? "bg-emerald-500/20 text-emerald-400" :
                  reg.payment.payment_status === "pending_verification" ? "bg-amber-500/20 text-amber-400" :
                  "bg-slate-500/20 text-slate-400"
                }>
                  {reg.payment.payment_status}
                </Badge>
              </div>
            </div>
          </div>
        )}

        {/* Actions based on stage */}
        <div className="pt-4 border-t border-slate-700">
          <h4 className="text-sm font-medium text-slate-300 mb-3">Actions</h4>
          
          {currentStage === "pending_approval" && (
            <div className="flex gap-2">
              <Button
                onClick={() => handleApproveRegistration(reg.id)}
                disabled={actionLoading === reg.id}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {actionLoading === reg.id ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Approve
              </Button>
              <Button
                variant="outline"
                onClick={() => setRejectDialog({ open: true, reg })}
                disabled={actionLoading === reg.id}
                className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/20"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </div>
          )}

          {currentStage === "awaiting_payment" && (
            <div className="space-y-3">
              <div className="text-center py-2">
                <CreditCard className="h-8 w-8 mx-auto mb-2 text-blue-400" />
                <p className="text-sm text-slate-400">
                  Waiting for parent to make payment
                </p>
                <p className="text-lg font-bold text-emerald-400 mt-1">
                  ${parseFloat(reg.total_fee || reg.total_amount || 0).toFixed(2)}
                </p>
              </div>
              
              {/* Show QR Code Button */}
              <Button 
                onClick={() => setQrDialog({ open: true, reg })}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                <QrCode className="h-4 w-4 mr-2" />
                View PayNow QR Code
              </Button>
              
              <Button variant="outline" size="sm" className="w-full">
                <Mail className="h-4 w-4 mr-2" />
                Send Payment Reminder
              </Button>
            </div>
          )}

          {currentStage === "payment_verification" && reg.payment && (
            <div className="flex gap-2">
              <Button
                onClick={() => handleVerifyPayment(reg.payment.id, "approve")}
                disabled={actionLoading === reg.payment.id}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {actionLoading === reg.payment.id ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Verify Payment
              </Button>
              <Button
                variant="outline"
                onClick={() => handleVerifyPayment(reg.payment.id, "reject")}
                disabled={actionLoading === reg.payment.id}
                className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/20"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </div>
          )}

          {currentStage === "child_assignment" && (
            <div className="space-y-2">
              <p className="text-sm text-slate-400">
                Payment verified. Assign children to training sessions.
              </p>
              <Link to={`/admin?tab=academy&section=players`}>
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  <Users className="h-4 w-4 mr-2" />
                  Go to Player Assignment
                </Button>
              </Link>
            </div>
          )}

          {currentStage === "complete" && (
            <div className="text-center py-4">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-400" />
              <p className="text-sm text-slate-400">
                Registration complete
              </p>
            </div>
          )}
        </div>

        {/* Timeline/History could go here */}
        <div className="text-xs text-slate-600 pt-4">
          Submitted: {formatDate(reg.created_at)}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] text-white flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold">Registration Pipeline</h1>
              <p className="text-slate-400 text-sm">Track registrations from signup to completion</p>
            </div>
          </div>
          <Button onClick={fetchPipeline} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stage Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {STAGES.map((stage, idx) => {
            const Icon = stage.icon;
            const count = counts[stage.key] || 0;
            const isActive = activeStage === stage.key;
            
            return (
              <button
                key={stage.key}
                onClick={() => setActiveStage(stage.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                  isActive
                    ? `bg-${stage.color}-500/20 border border-${stage.color}-500/50 text-${stage.color}-400`
                    : "bg-slate-800/50 border border-slate-700 text-slate-400 hover:border-slate-600"
                }`}
              >
                <Icon className={`h-4 w-4 ${isActive ? `text-${stage.color}-400` : ""}`} />
                <span className="text-sm font-medium">{stage.label}</span>
                <Badge className={`${
                  count > 0 ? `bg-${stage.color}-500/30 text-${stage.color}-300` : "bg-slate-700 text-slate-400"
                } text-xs`}>
                  {count}
                </Badge>
                {idx < STAGES.length - 1 && (
                  <ArrowRight className="h-3 w-3 text-slate-600 ml-1" />
                )}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-800/50 border-slate-700"
            />
          </div>
        </div>

        {/* Main Content - Split View */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Registration List */}
          <div className="lg:col-span-2">
            <Card className="bg-slate-800/30 border-slate-700">
              <div className="p-4 border-b border-slate-700">
                <h2 className="font-medium text-white flex items-center gap-2">
                  {STAGES.find(s => s.key === activeStage)?.label}
                  <Badge className="bg-slate-700">{counts[activeStage] || 0}</Badge>
                </h2>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[600px] overflow-y-auto">
                {filterRegistrations(pipeline?.[activeStage] || []).length === 0 ? (
                  <div className="col-span-2 text-center py-12 text-slate-400">
                    <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No registrations in this stage</p>
                  </div>
                ) : (
                  filterRegistrations(pipeline?.[activeStage] || []).map((reg) =>
                    renderRegistrationCard(reg, activeStage)
                  )
                )}
              </div>
            </Card>
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-1">
            <Card className="bg-slate-800/30 border-slate-700 sticky top-4">
              <div className="p-4 border-b border-slate-700">
                <h2 className="font-medium text-white">Registration Details</h2>
              </div>
              {renderDetailPanel()}
            </Card>
          </div>
        </div>

        {/* Reject Dialog */}
        <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ open, reg: open ? rejectDialog.reg : null })}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>Reject Registration</DialogTitle>
              <DialogDescription className="text-slate-400">
                Provide a reason for rejecting this registration.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Input
                placeholder="Reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialog({ open: false, reg: null })}>
                Cancel
              </Button>
              <Button
                onClick={handleRejectRegistration}
                disabled={!rejectionReason || actionLoading === rejectDialog.reg?.id}
                className="bg-red-600 hover:bg-red-700"
              >
                {actionLoading === rejectDialog.reg?.id ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Reject Registration
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* QR Code Dialog */}
        <Dialog open={qrDialog.open} onOpenChange={(open) => setQrDialog({ open, reg: open ? qrDialog.reg : null })}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <QrCode className="h-5 w-5 text-purple-400" />
                PayNow QR Code
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                {qrDialog.reg?.parentName || qrDialog.reg?.parent_name}
              </DialogDescription>
            </DialogHeader>
            
            {qrDialog.reg && (
              <div className="py-4 space-y-4">
                {/* QR Code Card - for download */}
                <div ref={pipelineQrRef} className="p-4 bg-slate-800 rounded-lg">
                  {/* QR Code */}
                  <div className="flex justify-center mb-4">
                    <div className="bg-white p-4 rounded-lg">
                      <QRCodeSVG
                        value={generatePayNowQRData(
                          qrDialog.reg.total_fee || qrDialog.reg.total_amount || 0,
                          qrDialog.reg.payment_qr_reference || qrDialog.reg.payment_reference || `SCC-${qrDialog.reg.id?.slice(0,8)}`
                        )}
                        size={180}
                        level="M"
                        includeMargin={false}
                      />
                    </div>
                  </div>

                  {/* Payment Details */}
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center p-2 bg-slate-900/50 rounded">
                      <span className="text-slate-400">Amount:</span>
                      <span className="text-xl font-bold text-emerald-400">
                        ${parseFloat(qrDialog.reg.total_fee || qrDialog.reg.total_amount || 0).toFixed(2)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center p-2 bg-slate-900/50 rounded">
                      <span className="text-slate-400">Reference:</span>
                      <code className="text-amber-400 font-mono text-xs">
                        {qrDialog.reg.payment_qr_reference || qrDialog.reg.payment_reference || `SCC-${qrDialog.reg.id?.slice(0,8)}`}
                      </code>
                    </div>

                    <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <p className="text-blue-300 text-xs">
                        <strong>PayNow to:</strong> Football And More Limited<br />
                        <strong>UEN:</strong> 201422384R (via Wise)
                      </p>
                    </div>
                    
                    {/* Child info */}
                    <div className="p-2 bg-slate-900/50 rounded text-xs text-slate-400">
                      <p><strong>Parent:</strong> {qrDialog.reg.parentName || qrDialog.reg.parent_name}</p>
                      <p><strong>Child:</strong> {qrDialog.reg.children?.map(c => c.child_name || `${c.given_name} ${c.last_name}`).join(', ')}</p>
                    </div>
                  </div>
                </div>
                
                {/* Copy Reference Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(
                    qrDialog.reg.payment_qr_reference || qrDialog.reg.payment_reference || `SCC-${qrDialog.reg.id?.slice(0,8)}`,
                    'qr-ref'
                  )}
                  className="w-full"
                >
                  {copiedField === 'qr-ref' ? (
                    <>
                      <Check className="h-4 w-4 mr-2 text-emerald-400" />
                      Reference Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Reference Number
                    </>
                  )}
                </Button>
              </div>
            )}
            
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setQrDialog({ open: false, reg: null })}>
                Close
              </Button>
              <Button 
                onClick={() => downloadPaymentQR(qrDialog.reg)}
                disabled={downloadingQr}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {downloadingQr ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Download QR
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
